import {
  ActiveInstallsHandler,
  AppAdminHandler,
  AppAdminSubmitFormHandler,
  AppAdminUploadLogoHandler,
  CategoryHandler,
  CollectionHandler,
  CreateTokenHandler,
  DeleteTokenHandler,
  getAlgoliaConfig,
  GetTokensHandler,
  HomeHandler,
  PartnerProfileHandler,
  SearchFiltersHandler,
  SearchResultHandler,
  SequenceTypes,
  VersionHistoryHandler,
} from '@atlassian/mpac-services-client';

import type { BootstrapConfig } from '../application-config/types/BootstrapConfig';
import type { StatsigClientType } from '@atlassian/mpac-feature-flags';
import type {
  IOrchestrationConfig,
  IOrchestrationConfigRecord,
} from '@atlassian/mpac-services-client';

const getConfig = <T extends IOrchestrationConfigRecord>(config: IOrchestrationConfig<T>) => config;

/**
 * Orchestration configuration for DLA
 *
 * `config` accepts both a config and a function returning config. This can be used for returning
 *  configuration based on some feature flag or some custom logic.
 */

const consoleManageAppsConfig = (bootstrapConfig: BootstrapConfig) => {
  return {
    GetMarketplaceAppActiveInstalls: {
      config: () => ({
        sequence: SequenceTypes.CUSTOM_ONLY,
        handler: new ActiveInstallsHandler(bootstrapConfig.mpacBaseUrl),
      }),
    },
    GetMarketplaceManageAppAdmin: {
      config: {
        sequence: SequenceTypes.CUSTOM_ONLY,
        handler: new AppAdminHandler(),
      },
    },
    UploadMarketplaceManageAppAdminTitleLogo: {
      config: {
        sequence: SequenceTypes.CUSTOM_ONLY,
        handler: new AppAdminUploadLogoHandler(),
      },
    },
    SubmitMarketplaceManageAppAdminData: {
      config: {
        sequence: SequenceTypes.CUSTOM_ONLY,
        handler: new AppAdminSubmitFormHandler(bootstrapConfig.mpacBaseUrl),
      },
    },
  };
};

// eslint-disable-next-line max-lines-per-function
const config = (bootstrapConfig: BootstrapConfig, statsigClient: StatsigClientType) => {
  const algoliaConfig = getAlgoliaConfig(
    bootstrapConfig.appConfig.commonConfig.algoliaConfig,
    bootstrapConfig.featureFlags,
    true // all aether pages need v2 index
  );

  const homeHandler = new HomeHandler(algoliaConfig);

  return getConfig({
    ...consoleManageAppsConfig(bootstrapConfig),
    GetMarketplaceSearch: {
      config: () => ({
        sequence: SequenceTypes.CUSTOM_ONLY,
        handler: new SearchResultHandler(algoliaConfig),
      }),
    },
    GetPartnerProfile: {
      middlewaresConfig: {
        cache: {
          primary: {
            ttlMs: 60_000,
            cacheKey: ({ variables }) => variables.vendorId,
          },
        },
      },
      config: () => ({
        sequence: SequenceTypes.PARALLEL,
        handler: new PartnerProfileHandler(algoliaConfig),
      }),
    },
    GetMarketplaceFilters: {
      config: () => ({
        sequence: SequenceTypes.CUSTOM_ONLY,
        handler: new SearchFiltersHandler(
          bootstrapConfig.stargateBaseUrl,
          bootstrapConfig.featureFlags,
          statsigClient
        ),
      }),
    },
    GetCollection: {
      middlewaresConfig: {
        cache: {
          primary: {
            ttlMs: 60_000,
            cacheKey: ({ variables }) => variables.slug,
          },
        },
      },
      config: () => ({
        sequence: SequenceTypes.PRIMARY_FIRST,
        handler: new CollectionHandler(algoliaConfig),
      }),
    },
    GetMarketplaceCategory: {
      middlewaresConfig: {
        cache: {
          primary: {
            ttlMs: 60_000, // todo: update TTL
            cacheKey: ({ variables }) => variables.slug,
          },
        },
      },
      config: {
        sequence: SequenceTypes.PRIMARY_FIRST,
        handler: new CategoryHandler(algoliaConfig),
      },
    },
    GetMarketplaceAppVersionHistory: {
      config: {
        sequence: SequenceTypes.PRIMARY_ONLY,
        handler: new VersionHistoryHandler(
          bootstrapConfig.mpacBaseUrl,
          bootstrapConfig.macBaseUrl,
          bootstrapConfig.wacBaseUrl
        ),
      },
    },
    GetMarketplaceHome: {
      middlewaresConfig: {
        cache: {
          primary: {
            ttlMs: 60_000,
            cacheKey: ({ variables }) => variables.productId,
          },
        },
      },
      config: ({ variables }) => ({
        sequence: variables.first === 1 ? SequenceTypes.CUSTOM_ONLY : SequenceTypes.PRIMARY_FIRST,
        handler: homeHandler,
      }),
    },
    CreateToken: {
      config: {
        sequence: SequenceTypes.CUSTOM_ONLY,
        handler: new CreateTokenHandler(bootstrapConfig.mpacBaseUrl),
      },
    },
    DeleteToken: {
      config: {
        sequence: SequenceTypes.CUSTOM_ONLY,
        handler: new DeleteTokenHandler(bootstrapConfig.mpacBaseUrl),
      },
    },
    GetTokens: {
      config: {
        sequence: SequenceTypes.CUSTOM_ONLY,
        handler: new GetTokensHandler(bootstrapConfig.mpacBaseUrl),
      },
    },
  });
};

export { config };
